import type { CrossroadsLog } from '@/api/hooks/useIntegrationLogs'
import { useAudit } from '@/api/hooks/useIntegrationLogs'
import type { components } from '@/types/api/v1'
import { Alert, Empty, Modal, Spin } from 'antd'
import AuditFlow from './AuditFlow'

type TranslationAudit = components['schemas']['TranslationAudit']

interface AuditModalViewProps {
  open: boolean
  onClose: () => void
  title: string
  audit: TranslationAudit | null | undefined
  isLoading: boolean
  error?: unknown
  routing?: CrossroadsLog['routing']
}

/**
 * The modal shell around {@link AuditFlow}: loading, error and empty states.
 *
 * Split out from {@link AuditModal} so callers that already hold an audit (or
 * load it some other way, such as the supply plan grid, whose audit is embedded
 * on the plan rather than looked up by saga id) can render the same view.
 */
export function AuditModalView({
  open,
  onClose,
  title,
  audit,
  isLoading,
  error,
  routing,
}: AuditModalViewProps) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={null}
      width={'60vw'}
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert
          type='error'
          showIcon
          message='Failed to load audit'
          description={error instanceof Error ? error.message : undefined}
        />
      ) : audit ? (
        <div style={{ height: '80vh' }}>
          <AuditFlow audit={audit} routing={routing} />
        </div>
      ) : (
        <Empty description='No audit available for this record' />
      )}
    </Modal>
  )
}

interface AuditModalProps {
  open: boolean
  onClose: () => void
  title: string
  logGroupId: string
  sagaId: string
  routing?: CrossroadsLog['routing']
}

export function AuditModal({
  open,
  onClose,
  title,
  logGroupId,
  sagaId,
  routing,
}: AuditModalProps) {
  const { data: audit, isLoading, error } = useAudit(logGroupId, sagaId)

  return (
    <AuditModalView
      open={open}
      onClose={onClose}
      title={title}
      audit={audit}
      isLoading={isLoading}
      error={error}
      routing={routing}
    />
  )
}
